import { Autocomplete as BaseAutocomplete } from '@base-ui/react/autocomplete'
import { Combobox as BaseCombobox } from '@base-ui/react/combobox'

import { cn } from '@gto/ui/cn'
import type { InputProps } from '@gto/ui/input'
import { InputGroup, inputVariants, isAriaInvalid } from '@gto/ui/input'
import { withPopupOpenMarker } from '@gto/ui/state-markers'

import { autocompleteVariants } from './autocomplete.styles'

type BaseComboboxInputProps = BaseCombobox.Input.Props

export type AutocompleteInputProps = Omit<BaseComboboxInputProps, 'render' | 'style' | 'size'> &
  InputProps

const autocompleteInput = ({
  className,
  variant,
  size,
  textAlign,
  id,
  startAddon,
  endAddon,
  ...props
}: AutocompleteInputProps) => {
  const { input } = inputVariants({ size, variant, textAlign })

  return (
    // Base UI's positioner anchors to the registered `inputGroupElement`, falling
    // back to the bare `<input>` when there is none. Rendering the DS InputGroup
    // THROUGH `InputGroup` is what registers it, so `--anchor-width` measures the
    // whole field instead of the input squeezed between its addons.
    <BaseCombobox.InputGroup
      className={withPopupOpenMarker()}
      render={
        <InputGroup
          className={className}
          endAddon={endAddon}
          invalid={isAriaInvalid(props['aria-invalid'])}
          size={size}
          startAddon={startAddon}
          variant={variant}
        />
      }
    >
      <BaseCombobox.Input
        className={input()}
        data-slot="input-group-control"
        id={id}
        {...props}
      />
    </BaseCombobox.InputGroup>
  )
}

const autocompleteClear = ({ className, ...props }: BaseCombobox.Clear.Props) => {
  const { clear } = autocompleteVariants()

  return (
    <BaseCombobox.Clear
      className={cn(clear(), className)}
      {...props}
    />
  )
}

const autocompleteEmpty = ({ className, ...props }: BaseCombobox.Empty.Props) => {
  const { empty } = autocompleteVariants()

  return (
    <BaseCombobox.Empty
      className={cn(empty(), className)}
      {...props}
    />
  )
}

const autocompleteGroup = ({ className, ...props }: BaseCombobox.Group.Props) => {
  const { group } = autocompleteVariants()

  return (
    <BaseCombobox.Group
      className={cn(group(), className)}
      {...props}
    />
  )
}

const autocompleteGroupLabel = ({ className, ...props }: BaseCombobox.GroupLabel.Props) => {
  const { groupLabel } = autocompleteVariants()

  return (
    <BaseCombobox.GroupLabel
      className={cn(groupLabel(), className)}
      {...props}
    />
  )
}

const autocompletePositioner = ({ className, ...props }: BaseCombobox.Positioner.Props) => {
  const { positioner } = autocompleteVariants()

  return (
    <BaseCombobox.Positioner
      className={cn(positioner(), className)}
      sideOffset={4}
      {...props}
    />
  )
}

const autocompletePopup = ({ className, ...props }: BaseCombobox.Popup.Props) => {
  const { popup } = autocompleteVariants()

  return (
    <BaseCombobox.Popup
      className={cn(popup(), className)}
      {...props}
    />
  )
}

const autocompleteList = ({ className, ...props }: BaseCombobox.List.Props) => {
  const { list } = autocompleteVariants()

  return (
    <BaseCombobox.List
      className={cn(list(), className)}
      {...props}
    />
  )
}

const autocompleteItem = ({ className, ...props }: BaseCombobox.Item.Props) => {
  const { item } = autocompleteVariants()

  return (
    <BaseCombobox.Item
      className={cn(item(), className)}
      {...props}
    />
  )
}

/**
 * Every part below `Root` is shared: `@base-ui/react/autocomplete` re-exports the
 * identical component objects from `@base-ui/react/combobox` (`AutocompleteItem`
 * IS `ComboboxItem`). Only the root — and `Value`, which reads from it — differ,
 * so the two namespaces below are one styled part set with two roots.
 */
const listboxParts = {
  Backdrop: BaseCombobox.Backdrop,
  Trigger: BaseCombobox.Trigger,
  Icon: BaseCombobox.Icon,
  Portal: BaseCombobox.Portal,
  Separator: BaseCombobox.Separator,
  Arrow: BaseCombobox.Arrow,
  Status: BaseCombobox.Status,
  Collection: BaseCombobox.Collection,
  Row: BaseCombobox.Row,
  Clear: autocompleteClear,
  Empty: autocompleteEmpty,
  Input: autocompleteInput,
  Group: autocompleteGroup,
  GroupLabel: autocompleteGroupLabel,
  Positioner: autocompletePositioner,
  Popup: autocompletePopup,
  List: autocompleteList,
  Item: autocompleteItem,
} as const

/**
 * Free text with suggestions: the value IS the input string, and the caller owns
 * whatever it maps to. Picking one item out of a fixed list is `Combobox` below —
 * `Autocomplete.Root` hardcodes `selectionMode: 'none'`, so it has no selection to
 * give you and every consumer ends up hand-rolling a string→item reverse map.
 */
export const Autocomplete = {
  ...listboxParts,
  Root: BaseAutocomplete.Root,
  Value: BaseAutocomplete.Value,
} as const

/**
 * Type-to-filter over a fixed list where the value is the ITEM, not the text.
 * Base UI keeps the input in sync with the selection, restores it on blur, and
 * shows the full list when a reopened field still reads the current selection —
 * all of which a consumer must otherwise rebuild by hand on `Autocomplete`.
 */
export const Combobox = {
  ...listboxParts,
  Root: BaseCombobox.Root,
  Value: BaseCombobox.Value,
} as const
